import React from 'react';
import styled from 'styled-components';

import { useMutation } from '@apollo/react-hooks';
import DOLink from '@do/walrus/DOLink';
import vars from '@do/walrus/scss/core/_variables.scss';
import SpinnerButton from '@do/walrus/SpinnerButton';
import LoadingState from '@do/walrus/states/Loading';
import Text from '@do/walrus/Text';
import TextInput from '@do/walrus/TextInput';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import * as Yup from 'yup';
import copy from '../copy';
import { SEND_SMS, TWO_FACTOR } from '../queries/queries';
import { ClickLink, ErrorBanner } from './Common';

import { LOGIN_REDIRECT_URL } from '../constants';
import { LoginStatus, Type2fa } from '../interfaces/login';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import {
  validationCopy,
  yupVerificationCharValidationFn,
  yupVerificationNumberValidation
} from '../utils/validation-utils';

const Spinner = styled(LoadingState)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  top: -1px;
  margin: 0 ${vars.space_1};
`;

const copy2fa = copy.form.two_factor_auth;

const VALIDATION_2FA = {
  authenticator: Yup.object().shape({
    code: yupVerificationNumberValidation
  }),
  sms: Yup.object().shape({
    code: yupVerificationCharValidationFn()
  }),
  recovery_codes: Yup.object().shape({
    code: yupVerificationCharValidationFn(true)
  })
};

interface SendSmsGqlResponse {
  sendSms2fa: {
    status: string;
  };
}

interface SendSmsGqlVariables {
  sendSms2faRequest: {
    session_data?: string;
  };
}

const TwoFactorSmsHeader = () => {
  const [sendSms, { loading }] = useMutation<
    SendSmsGqlResponse,
    SendSmsGqlVariables
  >(SEND_SMS);

  return (
    <Text>
      {copy2fa.sms.details}
      <br />
      <ClickLink
        disabled={loading}
        onClick={() =>
          sendSms({
            variables: {
              sendSms2faRequest: addSessionData()
            }
          })
        }
      >
        {copy2fa.sms.resend_link}
      </ClickLink>
      {loading && <Spinner />}
    </Text>
  );
};

interface Props {
  type: Type2fa;
  nextStepType?: Type2fa;
  changeStep: () => void;
  isSecondary?: boolean;
}

const TwoFactorFooter: React.FC<Props> = ({
  type,
  nextStepType,
  changeStep,
  isSecondary = false
}) => {
  const [sendSms, { loading }] = useMutation<
    SendSmsGqlResponse,
    SendSmsGqlVariables
  >(SEND_SMS);

  const hasNextPage = isSecondary || nextStepType;
  return (
    <>
      <Text className="do-u-mb--remove">{copy2fa[type].no_access}</Text>
      {hasNextPage && (
        <ClickLink
          disabled={loading}
          onClick={() => {
            if (nextStepType === 'sms') {
              return sendSms({
                variables: {
                  sendSms2faRequest: addSessionData()
                }
              }).finally(changeStep);
            }
            changeStep();
          }}
        >
          {nextStepType &&
            copy2fa[nextStepType][isSecondary ? 'forward_link' : 'backup_link']}
        </ClickLink>
      )}
      {loading && <Spinner />}
      {hasNextPage && <> {copy.form.or} </>}
      <DOLink path="/support">
        {copy.form.contact_support_link.replace(/./, (str: string) =>
          hasNextPage ? str : str.toUpperCase()
        )}
      </DOLink>
    </>
  );
};

interface TwoFactorGqlResponse {
  twoFactor: {
    status: LoginStatus;
    session_data: string;
    redirect_url: string;
  };
}

interface TwoFactorGqlVariables {
  twoFactorRequest: {
    type: Type2fa;
    code: string;
    session_data?: string;
  };
}

const TwoFactorAuthForm: React.FC<Props> = (props) => {
  const { type, isSecondary = false } = props;
  const [genericServerError, setGenericServerError] = React.useState(null);
  const [redirecting, setRedirecting] = React.useState(false);
  const [twoFactor, { loading }] = useMutation<
    TwoFactorGqlResponse,
    TwoFactorGqlVariables
  >(TWO_FACTOR);

  return (
    <>
      <h4>{copy2fa[isSecondary ? 'backup_title' : 'title']}</h4>
      {type === 'sms' ? (
        <TwoFactorSmsHeader />
      ) : (
        <Text>{copy2fa[type].details}</Text>
      )}

      <Formik
        initialValues={{
          code: ''
        }}
        validationSchema={VALIDATION_2FA[type]}
        onSubmit={(values, { setErrors }) => {
          setGenericServerError(null); /* Reset displayed error */

          twoFactor({
            variables: {
              twoFactorRequest: addSessionData({ type, ...values })
            }
          })
            .then((successResponse) => {
              const { status, redirect_url, session_data } = _get(
                successResponse,
                'data.twoFactorVerification'
              );

              saveSessionData(session_data);

              if (status === 'granted') {
                setRedirecting(true);
                window.location.assign(redirect_url || LOGIN_REDIRECT_URL);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.incorrect_two_factor);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
        render={({ errors, values, touched }) => (
          <Form className="do-u-pb--small">
            {genericServerError && <ErrorBanner error={genericServerError} />}

            <TextInput
              name="code"
              placeholder={`${copy2fa.placeholder_prefix} ${validationCopy.two_factor_auth[type].code_label}`}
              label={validationCopy.two_factor_auth[type].code_label}
              autoComplete="off"
              autoFocus
              touched={touched.code}
              error={errors.code}
              value={values.code}
            />
            <SpinnerButton
              className="do-u-mt--large"
              type="submit"
              fullWidth
              spinning={loading || redirecting}
              disabled={loading || redirecting}
              loadingMessage={copy.loading.login.belowButton}
            >
              {copy2fa.button_text}
            </SpinnerButton>

            <TwoFactorFooter {...props} />
          </Form>
        )}
      />
    </>
  );
};

export default TwoFactorAuthForm;
